import React, { useEffect, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { JsonInput } from '@shared/components/JsonInput';
import { useTranslation } from 'react-i18next';

import {
  CampaignWebhookDraft,
  CampaignWebhookValidationErrors,
} from '@features/Admin/Campaign/utils/campaign-webhook-mapper';

import CampaignWebhookForm from './CampaignWebhookForm';
import TabbedEditorDialog from './TabbedEditorDialog';

interface CampaignWebhookPopupProps {
  open: boolean;
  draft: CampaignWebhookDraft | null;
  errors: CampaignWebhookValidationErrors;
  disabled: boolean;
  isLoading: boolean;
  onChange: <K extends keyof CampaignWebhookDraft>(key: K, value: CampaignWebhookDraft[K]) => void;
  onAuthTypeChange: (authType: CampaignWebhookDraft['authType']) => void;
  onAuthFieldChange: (field: keyof CampaignWebhookDraft['authConfig'], value: string) => void;
  onHeaderChange: (index: number, field: 'key' | 'value', value: string) => void;
  onAddHeader: () => void;
  onRemoveHeader: (index: number) => void;
  onClose: () => void;
  onSave: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 1 }}>
    {value === index ? children : null}
  </Box>
);

const CampaignWebhookPopup: React.FC<CampaignWebhookPopupProps> = ({
  open,
  draft,
  errors,
  disabled,
  isLoading,
  onChange,
  onAuthTypeChange,
  onAuthFieldChange,
  onHeaderChange,
  onAddHeader,
  onRemoveHeader,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
    }
  }, [open]);

  useEffect(() => {
    if (errors.bodyTemplate) {
      setActiveTab(1);
    }
  }, [errors.bodyTemplate]);

  useEffect(() => {
    if (errors.events) setActiveTab(0);
  }, [errors.events]);

  if (!draft) {
    return null;
  }

  return (
    <TabbedEditorDialog
      open={open}
      onClose={onClose}
      title={
        draft.id ? t('campaignPage.webhooks.editWebhook') : t('campaignPage.webhooks.newWebhook')
      }
      tabs={[
        { label: t('campaignPage.webhooks.tabDetails'), disabled: isLoading },
        { label: t('campaignPage.webhooks.tabJson'), disabled: isLoading },
      ]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      disableClose={isLoading}
      paperSx={{ minHeight: '500px' }}
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={isLoading} sx={{ minWidth: 100 }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={disabled || isLoading}
            sx={{ minWidth: 140 }}>
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : draft.id ? (
              t('action.saveChange')
            ) : (
              t('action.create')
            )}
          </Button>
        </>
      }>
      <TabPanel value={activeTab} index={0}>
        <CampaignWebhookForm
          draft={draft}
          errors={errors}
          disabled={disabled}
          isSaving={isLoading}
          onChange={onChange}
          onAuthTypeChange={onAuthTypeChange}
          onAuthFieldChange={onAuthFieldChange}
          onHeaderChange={onHeaderChange}
          onAddHeader={onAddHeader}
          onRemoveHeader={onRemoveHeader}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('campaignPage.webhooks.jsonEditorHelp')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('campaignPage.webhooks.bodyTemplate')}
            </Typography>
            <Button
              size="small"
              onClick={() => onChange('bodyTemplate', '')}
              disabled={disabled || isLoading || !draft.bodyTemplate.trim()}
              sx={{ textTransform: 'none' }}>
              {t('campaignPage.webhooks.useDefaultPayload')}
            </Button>
          </Box>

          <JsonInput
            value={draft.bodyTemplate}
            onChange={(value) => onChange('bodyTemplate', value)}
            error={errors.bodyTemplate ? t(errors.bodyTemplate) : null}
            rows={16}
            disabled={disabled || isLoading}
            placeholder={t('campaignPage.webhooks.bodyTemplatePlaceholder')}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('campaignPage.webhooks.defaultPayloadHint')}
          </Typography>
        </Box>
      </TabPanel>
    </TabbedEditorDialog>
  );
};

export default CampaignWebhookPopup;
